import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon, CheckCircle as SuccessIcon, Error as ErrorIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { formatDateTime, formatRelativeTime } from '../../utils/dateUtils';
import { describeApiError } from './agentMeta';

const prettify = (value) => (value ? String(value).replace(/_/g, ' ') : '—');

/** Per-agent enrichment log viewer. */
const ActivityTab = ({ agents = [] }) => {
  const [agentId, setAgentId] = useState('');
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!agentId && agents.length) setAgentId(agents[0].id || agents[0]._id);
  }, [agents, agentId]);

  const load = useCallback(async () => {
    if (!agentId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.agents.getLogs(agentId, { page: page + 1, limit });
      setLogs(res.data.logs || []);
      setTotal((res.data.pagination && res.data.pagination.total) || 0);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load activity').message);
    } finally {
      setLoading(false);
    }
  }, [agentId, page, limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Agent</InputLabel>
          <Select value={agentId} label="Agent" onChange={(e) => { setAgentId(e.target.value); setPage(0); }}>
            {agents.map((a) => <MenuItem key={a.id || a._id} value={a.id || a._id}>{a.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <span><IconButton onClick={load} disabled={loading || !agentId}>{loading ? <CircularProgress size={20} /> : <RefreshIcon />}</IconButton></span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Result</TableCell>
              <TableCell align="right">Duration</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No agents configured.</Typography></TableCell></TableRow>
            ) : loading && logs.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No activity recorded for this agent yet.</Typography></TableCell></TableRow>
            ) : logs.map((log) => {
              const success = !!(log.response && log.response.success);
              const errorMessage = log.response && log.response.error && (log.response.error.message || log.response.error);
              const duration = log.processing && log.processing.duration;
              return (
                <TableRow key={log.id || log._id} hover>
                  <TableCell>
                    <Tooltip title={formatDateTime(log.createdAt)}>
                      <Typography variant="body2">{formatRelativeTime(log.createdAt)}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{prettify(log.enrichmentType)}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{prettify(log.targetType)}</Typography>
                    {log.targetId && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 160 }} noWrap>{log.targetId}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={success ? <SuccessIcon /> : <ErrorIcon />}
                      label={success ? 'Success' : 'Failed'}
                      color={success ? 'success' : 'error'}
                      variant="outlined"
                    />
                    {log.processing && log.processing.rateLimited && <Chip size="small" label="rate limited" color="warning" variant="outlined" sx={{ ml: 0.5 }} />}
                    {log.processing && log.processing.cached && <Chip size="small" label="cached" variant="outlined" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell align="right"><Typography variant="body2">{duration !== undefined && duration !== null ? `${duration} ms` : '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{prettify(log.triggerType)}</Typography></TableCell>
                  <TableCell>
                    {errorMessage ? (
                      <Tooltip title={typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}>
                        <Typography variant="body2" color="error" sx={{ maxWidth: 320 }} noWrap>
                          {typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}
                        </Typography>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default ActivityTab;
